import * as React from "react";
import { useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
   Box,
   Button,
   CircularProgress,
   IconButton,
   ListItemIcon,
   ListItemText,
   Menu,
   MenuItem,
   Tooltip,
   Typography
} from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { RangeCommand } from "./RangeListTypes";
import { SavingUI } from "../../SavingProvider";

interface RangeCommandsProps<T> {
   /** Array of selected row IDs */
   selectedIds: string[];
   /** Commands to display */
   commands?: RangeCommand<T>[];
   /** Callback to clear selection */
   onClearSelection: () => void;
   /** Callback after command execution completes with optional partial updates */
   onCommandExecuted?: (updates?: Partial<T>[]) => void;
}

type AnchorState<T> = {
   element: HTMLElement | null;
   command: RangeCommand<T>;
} | null;

export const RangeCommands = <T extends object>({
   selectedIds,
   commands,
   onClearSelection,
   onCommandExecuted
}: RangeCommandsProps<T>) => {
   const classes = useStyles();
   const [anchorEl, setAnchorEl] = useState<AnchorState<T>>(null);
   const [executing, setExecuting] = useState<string>('');

   const handleCommandClick = (cmd: RangeCommand<T>, event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl({
         element: event.currentTarget,
         command: cmd
      });
   };

   const handleMenuClose = () => {
      setAnchorEl(null);
   };

   const onExecute = async (cmd: RangeCommand<T>) => {
      if (!cmd.execute) return;

      setExecuting(cmd.id);
      handleMenuClose();

      try {
         const updates = await SavingUI.process(
            async () => cmd.execute!(selectedIds),
            'Done',
            cmd.title + '...'
         );
         onCommandExecuted?.(updates || undefined);
      } catch (error) {
         console.error('Error executing command:', error);
      } finally {
         setExecuting('');
      }
   };

   const menuOpen = Boolean(anchorEl);

   return (
      <div className={classes.container}>
         <Typography className={classes.title} color="inherit" variant="subtitle1" component="div">
            {selectedIds.length} selected
            <IconButton
               size="small"
               onClick={onClearSelection}
            >
               <ClearIcon fontSize="inherit" />
            </IconButton>
         </Typography>
         <div className={classes.commands}>
            {commands?.map(cmd => {
               const onClick = async () => await onExecute(cmd);
               return (
                  <Box key={'command_' + cmd.id}>
                     <Tooltip title={cmd.title}>
                        <Button
                           aria-label={cmd.title}
                           onClick={cmd.children ? (e) => handleCommandClick(cmd, e) : onClick}
                           size="small"
                           startIcon={
                              cmd.children ? undefined :
                                 executing === cmd.id ? <CircularProgress size={18} /> :
                                    <cmd.icon />
                           }
                           endIcon={cmd.children ? <ArrowDropDownIcon /> : undefined}
                        >
                           {cmd.title}
                        </Button>
                     </Tooltip>
                  </Box>
               );
            })}
         </div>

         {anchorEl && (
            <Menu
               id={"menu_" + anchorEl.command.id}
               open={menuOpen}
               anchorEl={anchorEl.element}
               onClose={handleMenuClose}
            >
               {anchorEl.command.children?.map(childCmd => (
                  <MenuItem
                     key={'cmd_' + childCmd.id}
                     onClick={async () => await onExecute(childCmd)}
                  >
                     <ListItemIcon>
                        {executing === childCmd.id ? <CircularProgress size={18} /> : <childCmd.icon />}
                     </ListItemIcon>
                     <ListItemText>
                        {childCmd.title}
                     </ListItemText>
                  </MenuItem>
               ))}
            </Menu>
         )}
      </div>
   );
};

const useStyles = makeStyles({
   container: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: '#f8f8f8',
      minHeight: '56px',
      boxSizing: 'border-box',
      paddingLeft: '8px',
      paddingRight: '8px',
   },
   title: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
   },
   commands: {
      display: 'flex',
      flexDirection: 'row',
      gap: '10px',
   },
});
